import React, { useState, useCallback } from 'react';
import { Box, Button, IconButton, CircularProgress, Alert } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CropIcon from '@mui/icons-material/Crop';
import ImageCropDialog from './ImageCropDialog';
import { compressImage, needsCompression } from './imageUtils';

interface ImageUploadProps {
  value: File | File[] | null;
  onChange: (files: any) => void;
  label?: string;
  helperText?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number;
  error?: boolean;
  errorMessage?: string;
  enableCrop?: boolean;
  cropAspectRatio?: number;
  autoCompress?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  label = 'Arraste uma imagem ou clique para selecionar',
  helperText,
  multiple = false,
  maxFiles = 10,
  maxSize = 2097152, // 2MB
  error = false,
  errorMessage,
  enableCrop = false,
  cropAspectRatio = 2, // 2:1 padrão (1200x600)
  autoCompress = true,
}) => {
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const maxSizeMB = Math.max(0, Math.round((maxSize / (1024 * 1024)) * 10) / 10);
  const maxSizeMBPretty = Number.isInteger(maxSizeMB) ? String(maxSizeMB) : maxSizeMB.toFixed(1);
  const allowedExtensions = ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.heic'];

  const displayedErrorMessage = localError ?? (error && errorMessage ? errorMessage : null);

  const processImage = useCallback(async (file: File): Promise<File> => {
    let processedFile = file;

    // Comprimir imagem se necessário e se autoCompress estiver habilitado
    if (autoCompress && needsCompression(file, maxSize / (1024 * 1024))) {
      setProcessingMessage('Comprimindo imagem...');
      processedFile = await compressImage(processedFile, {
        maxSizeMB: maxSize / (1024 * 1024),
        maxWidthOrHeight: 1920,
      });
    }

    return processedFile;
  }, [autoCompress, maxSize]);

  const validateProcessedFileOrSetError = useCallback((file: File) => {
    if (!file.type?.startsWith('image/')) {
      setLocalError('Formato inválido. Envie uma imagem (JPEG, PNG, WEBP ou GIF).');
      return false;
    }

    if (file.size > maxSize) {
      setLocalError(`Imagem excede o limite de ${maxSizeMBPretty}MB.`);
      return false;
    }

    return true;
  }, [maxSize, maxSizeMBPretty]);

  const handleFileSelected = useCallback(async (acceptedFiles: File[]) => {
    if (multiple) {
      setProcessing(true);
      setProcessingMessage('Processando imagens...');
      setLocalError(null);
      
      try {
        const processedFiles = await Promise.all(
          acceptedFiles.map(file => processImage(file))
        );
        
        const currentFiles = Array.isArray(value) ? value : [];
        const newFiles = [...currentFiles, ...processedFiles];
        const limitedFiles = newFiles.slice(0, maxFiles);

        // Só validar o que acabou de ser enviado/processado (os itens antigos podem ser URLs/string)
        const hasInvalid = processedFiles.some((file) => !validateProcessedFileOrSetError(file));
        if (hasInvalid) return;

        onChange(limitedFiles);
      } catch (error) {
        console.error('Erro ao processar imagens:', error);
        setLocalError('Não foi possível processar uma ou mais imagens. Tente novamente.');
      } finally {
        setProcessing(false);
        setProcessingMessage(null);
      }
    } else {
      const file = acceptedFiles[0];
      setLocalError(null);
      
      // Se crop estiver habilitado, mostrar dialog de crop
      if (enableCrop) {
        const imageUrl = URL.createObjectURL(file);
        setImageToCrop(imageUrl);
        setCropDialogOpen(true);
      } else {
        // Processar imagem normalmente (comprimir se necessário)
        setProcessing(true);
        setProcessingMessage('Processando imagem...');
        
        try {
          const processedFile = await processImage(file);
          const isValid = validateProcessedFileOrSetError(processedFile);
          if (!isValid) return;

          onChange(processedFile);
        } catch (error) {
          console.error('Erro ao processar imagem:', error);
          setLocalError('Não foi possível processar a imagem. Tente novamente.');
        } finally {
          setProcessing(false);
          setProcessingMessage(null);
        }
      }
    }
  }, [multiple, value, maxFiles, onChange, enableCrop, processImage, validateProcessedFileOrSetError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': allowedExtensions
    },
    multiple,
    maxFiles,
    maxSize: enableCrop ? undefined : maxSize, // Se tiver crop, não limitar tamanho aqui
    onDrop: handleFileSelected,
    onDropRejected: (fileRejections) => {
      const rejection = fileRejections?.[0];
      const codes = rejection?.errors?.map((e) => e.code) ?? [];

      if (codes.includes('too-many-files')) {
        setLocalError(`Selecione no máximo ${maxFiles} imagens.`);
        return;
      }

      if (codes.includes('file-too-large')) {
        setLocalError(`Imagem excede o limite de ${maxSizeMBPretty}MB.`);
        return;
      }

      if (codes.includes('file-invalid-type') || codes.includes('file-invalid')) {
        setLocalError('Formato inválido. Envie uma imagem (JPEG, PNG, WEBP ou GIF).');
        return;
      }

      setLocalError('Arquivo inválido. Verifique o formato e o tamanho.');
    },
  });

  const handleCropComplete = useCallback(async (croppedFile: File) => {
    setProcessing(true);
    setProcessingMessage('Finalizando...');
    setLocalError(null);
    
    try {
      // Comprimir a imagem recortada se necessário
      const finalFile = await processImage(croppedFile);
      const isValid = validateProcessedFileOrSetError(finalFile);
      if (!isValid) return;

      onChange(finalFile);
    } catch (error) {
      console.error('Erro ao processar imagem recortada:', error);
      setLocalError('Não foi possível finalizar o recorte. Tente novamente.');
    } finally {
      setProcessing(false);
      setProcessingMessage(null);
      setCropDialogOpen(false);
      if (imageToCrop) {
        URL.revokeObjectURL(imageToCrop);
        setImageToCrop(null);
      }
    }
  }, [onChange, processImage, imageToCrop, validateProcessedFileOrSetError]);

  const handleCropCancel = useCallback(() => {
    setCropDialogOpen(false);
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);
    }
  }, [imageToCrop]);

  const handleCropClick = useCallback(() => {
    if (!value || multiple) return;
    
    if (value instanceof File) {
      const imageUrl = URL.createObjectURL(value);
      setImageToCrop(imageUrl);
      setCropDialogOpen(true);
    } else if (typeof value === 'string') {
      setImageToCrop(value);
      setCropDialogOpen(true);
      }
  }, [value, multiple]);

  const handleRemoveFile = (index?: number) => {
    if (multiple && Array.isArray(value)) {
      const newFiles = [...value];
      newFiles.splice(index as number, 1);
      onChange(newFiles);
    } else {
      onChange(null);
    }
  };

  const renderPreview = () => {
  if (!value) return null;

  if (multiple && Array.isArray(value)) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        {value.map((file, index) => {
          const previewUrl = file instanceof File ? URL.createObjectURL(file) : file;
          return (
            <Box
              key={index}
              sx={{
                position: 'relative',
                width: 100,
                height: 100,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid #ddd'
              }}
            >
              <Box
                component="img"
                src={previewUrl}
                alt={`Preview ${index}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'rgba(255,255,255,0.7)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                  }
                }}
                onClick={() => handleRemoveFile(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
      </Box>
    );
  } else if (!multiple && value) {
    const previewUrl = (value instanceof File ? URL.createObjectURL(value) : value) as string;

    return (
      <Box sx={{ mt: 2, position: 'relative' }}>
        <Box
          component="img"
          src={previewUrl}
          alt="Preview"
          sx={{ 
            width: '100%',
            maxHeight: 200,
            objectFit: 'contain',
            borderRadius: 1,
            border: '1px solid #ddd'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            display: 'flex',
            gap: 1,
          }}
        >
          {enableCrop && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<CropIcon />}
              onClick={handleCropClick}
            >
              Recortar
            </Button>
          )}
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleRemoveFile()}
        >
          Remover
        </Button>
        </Box>
      </Box>
    );
  }

  return null;
};


  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: displayedErrorMessage ? 'error.main' : isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: processing ? 'wait' : 'pointer',
          transition: 'all 0.2s ease',
          opacity: processing ? 0.6 : 1,
          pointerEvents: processing ? 'none' : 'auto',
          '&:hover': {
            bgcolor: processing ? 'action.hover' : 'action.hover',
            borderColor: processing ? 'grey.400' : 'primary.main'
          }
        }}
      >
        <input {...getInputProps()} disabled={processing} />
        {processing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={40} />
            {processingMessage && (
              <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
                {processingMessage}
              </Box>
            )}
          </Box>
        ) : (
          <>
        <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Box sx={{ typography: 'body1', mb: 1 }}>{label}</Box>
        {helperText && (
          <Box sx={{ typography: 'caption', color: 'text.secondary' }}>
            {helperText}
          </Box>
            )}
          </>
        )}
      </Box>
      
      {displayedErrorMessage && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {displayedErrorMessage}
        </Alert>
      )}
      
      {renderPreview()}
      
      {multiple && Array.isArray(value) && value.length > 0 && (
        <Box sx={{ mt: 1, typography: 'caption', color: 'text.secondary' }}>
          {value.length} {value.length === 1 ? 'arquivo selecionado' : 'arquivos selecionados'} 
          (máximo: {maxFiles})
        </Box>
      )}

      {imageToCrop && (
        <ImageCropDialog
          open={cropDialogOpen}
          imageSrc={imageToCrop}
          aspectRatio={cropAspectRatio}
          onClose={handleCropCancel}
          onCropComplete={handleCropComplete}
        />
      )}
    </Box>
  );
};

export default ImageUpload;
